// FE-047 — Recent Playbook applications modal.
//
// Contextual surface opened from the Playbook editor's CTA next to
// Apply Playbook. Lists the most recent ``PlaybookApplication`` rows
// for the current Playbook with enough metadata to:
//
// * jump into a Commitment Tracker filtered by Account + Plan year +
//   ``source=playbook`` (the rows the apply spawned), and
// * undo a recent active application while it's still inside the
//   24h undo window.
//
// Reverted / expired rows render a static badge instead of an Undo
// button so the broker can see history without an action that would
// only fail server-side.

import { ExternalLink, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  usePlaybookApplicationsQuery,
  useRevertPlaybookApplicationMutation,
} from "./hooks";
import type { PlaybookApplicationRow } from "./api";


export function RecentPlaybookApplicationsModal({
  playbookId,
  playbookName,
  open,
  onClose,
}: {
  playbookId: string;
  playbookName: string;
  open: boolean;
  onClose: () => void;
}) {
  const query = usePlaybookApplicationsQuery(playbookId, open);
  const revertMutation = useRevertPlaybookApplicationMutation();
  const [pendingRevertId, setPendingRevertId] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  if (!open) return null;

  function handleRevert(row: PlaybookApplicationRow): void {
    setServerError(null);
    setPendingRevertId(row.application_id);
    revertMutation.mutate(row.application_id, {
      onSuccess: () => {
        setPendingRevertId(null);
        // The mutation's onSuccess invalidates the applications
        // query — react-query refetches, surfacing revoked_at and
        // can_undo=false on the row.
      },
      onError: (err) => {
        setPendingRevertId(null);
        setServerError(
          err instanceof Error
            ? err.message
            : "Couldn't revert this application.",
        );
      },
    });
  }

  const rows = query.data?.rows ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="recent-applications-title"
        data-testid="recent-applications-modal"
        className="flex w-full max-w-2xl flex-col rounded-lg border border-border-subtle bg-canvas shadow-xl"
      >
        <header className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
          <div>
            <h2
              id="recent-applications-title"
              className="text-base font-semibold text-text-primary"
            >
              Recent applications
            </h2>
            <p className="mt-0.5 text-xs text-text-tertiary">
              {playbookName}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-md p-1 text-text-secondary hover:bg-recessed"
          >
            <X size={18} />
          </button>
        </header>
        <div className="flex flex-col gap-3 p-5 text-sm">
          {query.isPending && (
            <p
              data-testid="recent-applications-loading"
              className="text-xs text-text-tertiary"
            >
              Loading recent applications…
            </p>
          )}
          {query.isError && (
            <p
              role="alert"
              data-testid="recent-applications-error"
              className="rounded-md bg-ember-tint px-3 py-2 text-xs text-ember-ink"
            >
              Couldn't load recent applications.
            </p>
          )}
          {query.isSuccess && rows.length === 0 && (
            <p
              data-testid="recent-applications-empty"
              className="text-xs text-text-tertiary"
            >
              This Playbook hasn't been applied yet.
            </p>
          )}
          {query.isSuccess && rows.length > 0 && (
            <ul
              data-testid="recent-applications-rows"
              className="flex flex-col gap-2"
            >
              {rows.map((row) => (
                <ApplicationRow
                  key={row.application_id}
                  row={row}
                  onRevert={handleRevert}
                  isReverting={
                    pendingRevertId === row.application_id
                    && revertMutation.isPending
                  }
                  onCloseModal={onClose}
                />
              ))}
            </ul>
          )}
          {serverError && (
            <p
              role="alert"
              data-testid="recent-applications-revert-error"
              className="rounded-md bg-ember-tint px-3 py-2 text-xs text-ember-ink"
            >
              {serverError}
            </p>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t border-border-subtle px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed"
          >
            Close
          </button>
        </footer>
      </section>
    </div>
  );
}


function ApplicationRow({
  row, onRevert, isReverting, onCloseModal,
}: {
  row: PlaybookApplicationRow;
  onRevert: (row: PlaybookApplicationRow) => void;
  isReverting: boolean;
  onCloseModal: () => void;
}) {
  // ``playbook_application_id`` scopes the Tracker to exactly the
  // rows this application spawned — the precise answer to "show me
  // the Commitments this Apply created" even when the same Account
  // + Plan year was applied multiple times. ``source=playbook`` is
  // a broader filter for "all Playbook-generated work" but isn't
  // what we want from an application-row deep link.
  const trackerHref = (
    "/dashboard/commitment-tracker"
    + `?playbook_application_id=${encodeURIComponent(row.application_id)}`
  );
  const reverted = row.revoked_at !== null;
  const undoExpired = !reverted && !row.can_undo;
  return (
    <li
      data-testid={`recent-applications-row-${row.application_id}`}
      className="flex flex-col gap-1 rounded-md border border-border-subtle bg-canvas/40 px-3 py-2"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-text-primary">
            {row.account_name ?? "Account"}
          </span>
          <span className="text-[11px] text-text-tertiary">
            {row.plan_year_label ?? "—"}
          </span>
        </div>
        <span
          data-testid={`recent-applications-status-${row.application_id}`}
          className={[
            "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
            reverted
              ? "bg-recessed text-text-secondary"
              : "bg-teal-tint text-teal",
          ].join(" ")}
        >
          {reverted ? "Reverted" : "Active"}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-text-tertiary">
        <span>
          Applied {row.applied_at ? formatDateTime(row.applied_at) : "—"}
        </span>
        <span aria-hidden="true">·</span>
        <span>
          by {row.applied_by_name ?? row.applied_by_email ?? "—"}
        </span>
        <span aria-hidden="true">·</span>
        <span>
          Created {row.created_count} Commitment
          {row.created_count === 1 ? "" : "s"}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-3">
        <Link
          to={trackerHref}
          data-testid={`recent-applications-view-${row.application_id}`}
          onClick={onCloseModal}
          className="inline-flex items-center gap-1 text-xs font-medium text-teal hover:underline"
        >
          View Commitments
          <ExternalLink size={11} aria-hidden="true" />
        </Link>
        {row.can_undo ? (
          <button
            type="button"
            data-testid={`recent-applications-undo-${row.application_id}`}
            onClick={() => onRevert(row)}
            disabled={isReverting}
            className="rounded-md border border-border-subtle px-2 py-1 text-xs text-text-primary hover:bg-recessed disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isReverting ? "Reverting…" : "Undo"}
          </button>
        ) : reverted ? null : undoExpired ? (
          <span
            data-testid={`recent-applications-expired-${row.application_id}`}
            className="text-[11px] text-text-tertiary"
          >
            Undo expired
          </span>
        ) : null}
      </div>
    </li>
  );
}


function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
